/**
 * CRMP - Compliance Risk Management Plan
 * 
 * Shows extracted obligations in table/Excel format:
 * - Filter by regulatory theme (AML, Cybersecurity, etc.)
 * - View obligations with risk ratings
 * - Review queue for human oversight
 * - Export capabilities
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ListChecks, CheckCircle, XCircle, Eye, 
  Scales, Shield, Buildings, ArrowRight,
  FileText, Funnel, ChartBar, Database, Robot,
  ClipboardText, Target, Warning, Lightning,
  CaretRight, MagnifyingGlass, Plus, Clock, Users,
  ChartLine, Gauge, FolderOpen, BookOpen, Brain,
  CaretDown, CaretUp, ArrowsClockwise, Export,
  Coins, Gavel, UsersFour, Lock, Leaf, Bank, Globe, Handshake,
  Table, Rows, DownloadSimple, Funnel as FunnelIcon
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';

// Regulatory Themes
const REGULATORY_THEMES = [
  { id: 'AML_CFT_PF', label: 'AML/CFT/PF', icon: Coins, color: '#F59E0B' },
  { id: 'Anti_Bribery', label: 'Anti Bribery', icon: Gavel, color: '#EF4444' },
  { id: 'AI_Governance', label: 'AI Governance', icon: Brain, color: '#A855F7' },
  { id: 'Capital_Liquidity', label: 'Capital & Liquidity', icon: Bank, color: '#0891B2' },
  { id: 'Consumer_Protection', label: 'Consumer Protection', icon: UsersFour, color: '#3B82F6' },
  { id: 'Conduct', label: 'Conduct', icon: Handshake, color: '#14B8A6' },
  { id: 'Cybersecurity', label: 'Cybersecurity', icon: Shield, color: '#06B6D4' },
  { id: 'FATCA_CRS', label: 'FATCA/CRS', icon: Globe, color: '#6366F1' },
  { id: 'Info_Privacy_Security', label: 'Information Privacy & Security', icon: Lock, color: '#EC4899' },
  { id: 'IT_Governance', label: 'IT Governance', icon: Database, color: '#0EA5E9' },
  { id: 'Social_Impact', label: 'Social Impact', icon: Leaf, color: '#10B981' },
  { id: 'Governance', label: 'Governance', icon: Buildings, color: '#D4A843' }
];

// Risk rating colors
const RISK_COLORS = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  negligible: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/30' },
};

// Status colors
const STATUS_COLORS = {
  pending_review: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Pending Review' },
  approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Approved' },
  rejected: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', label: 'Rejected' },
};

const CRMPPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [selectedTheme, setSelectedTheme] = useState(searchParams.get('theme') || 'all');
  const [activeView, setActiveView] = useState('obligations'); // obligations, review, controls, stats
  const [obligations, setObligations] = useState([]);
  const [controls, setControls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [expandedObligation, setExpandedObligation] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  // Get current theme config
  const currentTheme = REGULATORY_THEMES.find(t => t.id === selectedTheme);

  useEffect(() => {
    fetchData();
  }, [selectedTheme, activeView, filterStatus, filterRisk]);

  useEffect(() => {
    if (selectedTheme !== 'all') {
      setSearchParams({ theme: selectedTheme });
    } else {
      setSearchParams({});
    }
  }, [selectedTheme]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (selectedTheme && selectedTheme !== 'all') {
        params.append('theme', selectedTheme);
      }
      if (filterStatus && filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterRisk && filterRisk !== 'all') {
        params.append('risk_rating', filterRisk);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      params.append('limit', '200');

      // Fetch obligations
      const obligationsRes = await axios.get(`${API}/crmp/obligations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObligations(obligationsRes.data.obligations || []);

      // Fetch stats
      const statsRes = await axios.get(`${API}/crmp/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Fetch controls if on controls tab
      if (activeView === 'controls') {
        const controlsParams = new URLSearchParams();
        if (selectedTheme && selectedTheme !== 'all') {
          controlsParams.append('theme', selectedTheme);
        }
        const controlsRes = await axios.get(`${API}/crmp/controls?${controlsParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setControls(controlsRes.data.controls || []);
      }
    } catch (error) {
      console.error('Failed to fetch CRMP data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load CRMP data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (obligationId) => {
    setApprovingId(obligationId);
    try {
      await axios.post(`${API}/crmp/review-queue/approve/${obligationId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Obligation approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve obligation');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (obligationId) => {
    setRejectingId(obligationId);
    try {
      await axios.post(`${API}/crmp/review-queue/reject/${obligationId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Obligation rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject obligation');
    } finally {
      setRejectingId(null);
    }
  };

  // Filter obligations based on search
  const filteredObligations = useMemo(() => {
    if (!searchQuery.trim()) return obligations;
    const query = searchQuery.toLowerCase();
    return obligations.filter(ob => 
      ob.verbatim_text?.toLowerCase().includes(query) ||
      ob.plain_language_summary?.toLowerCase().includes(query) ||
      ob.section_reference?.toLowerCase().includes(query) ||
      ob.source_document_name?.toLowerCase().includes(query)
    );
  }, [obligations, searchQuery]);

  // Group obligations by document
  const groupedByDocument = useMemo(() => {
    const groups = {};
    filteredObligations.forEach(ob => {
      const docName = ob.source_document_name || 'Unknown Document';
      if (!groups[docName]) {
        groups[docName] = [];
      }
      groups[docName].push(ob);
    });
    return groups;
  }, [filteredObligations]);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1 flex">
        {/* Theme Sidebar */}
        <div className="w-64 border-r border-border bg-surface p-4 min-h-screen">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardText size={20} className="text-gold" weight="duotone" />
            <h3 className="text-sm font-semibold text-text-primary">CRMP Themes</h3>
          </div>
          
          {/* All Themes Option */}
          <button
            onClick={() => setSelectedTheme('all')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all ${
              selectedTheme === 'all'
                ? 'bg-gold/10 border border-gold/30 text-gold'
                : 'hover:bg-surface text-text-secondary hover:text-text-primary'
            }`}
          >
            <Table size={18} />
            <span className="text-sm">All Themes</span>
            {stats && (
              <span className="ml-auto text-xs bg-background px-2 py-0.5 rounded">
                {stats.obligations?.total || 0}
              </span>
            )}
          </button>
          
          <div className="space-y-1 mt-2">
            {REGULATORY_THEMES.map((theme) => {
              const Icon = theme.icon;
              const isActive = selectedTheme === theme.id;
              const count = stats?.by_theme?.[theme.id] || 0;
              
              return (
                <button
                  key={theme.id}
                  onClick={() => setSelectedTheme(theme.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gold/10 border border-gold/30'
                      : 'hover:bg-background'
                  }`}
                  data-testid={`crmp-theme-${theme.id}`}
                >
                  <Icon 
                    size={18} 
                    style={{ color: isActive ? theme.color : 'var(--text-tertiary)' }}
                  />
                  <span className={`text-sm ${isActive ? 'text-text-primary font-medium' : 'text-text-secondary'}`}>
                    {theme.label}
                  </span>
                  {count > 0 && (
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
                      isActive ? 'bg-gold/20 text-gold' : 'bg-background text-text-tertiary'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Quick Stats */}
          {stats && (
            <div className="mt-6 pt-4 border-t border-border space-y-2">
              <div className="text-xs text-text-tertiary uppercase tracking-wide mb-2">Quick Stats</div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Pending Review</span>
                <span className="text-amber-400 font-medium">{stats.obligations?.pending_review || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Approved</span>
                <span className="text-emerald-400 font-medium">{stats.obligations?.approved || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Controls</span>
                <span className="text-blue-400 font-medium">{stats.controls?.total || 0}</span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Breadcrumb */}
          <div className="px-6 pt-4 bg-surface">
            <PageBreadcrumb items={[
              { label: 'CRMP', href: '/crmp' },
              { label: currentTheme ? currentTheme.label : 'All Themes' }
            ]} />
          </div>

          {/* Header */}
          <div className="px-6 pb-6 border-b border-border bg-surface">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center">
                  <ClipboardText size={28} className="text-gold" weight="duotone" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-medium text-text-primary">
                    {currentTheme ? `${currentTheme.label} CRMP` : 'Compliance Risk Management Plan'}
                  </h1>
                  <p className="text-sm text-text-secondary">
                    {filteredObligations.length} obligations extracted from regulatory documents
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/aura-master-library')}
                  className="px-4 py-2 bg-surface border border-border text-text-primary rounded-lg hover:border-gold/50 transition-all flex items-center gap-2"
                >
                  <FolderOpen size={18} />
                  Library
                </button>
                <button
                  onClick={() => toast.info('Export coming soon')}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-lg hover:bg-gold-hover transition-all flex items-center gap-2"
                >
                  <DownloadSimple size={18} />
                  Export
                </button>
              </div>
            </div>

            {/* View Tabs */}
            <div className="flex items-center gap-2">
              {[
                { id: 'obligations', label: 'Obligations', icon: FileText, count: filteredObligations.length },
                { id: 'review', label: 'Review Queue', icon: Eye, count: stats?.obligations?.pending_review || 0 },
                { id: 'controls', label: 'Controls', icon: Shield, count: stats?.controls?.total || 0 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all ${
                    activeView === tab.id
                      ? 'bg-gold/10 text-gold border border-gold/30'
                      : 'text-text-secondary hover:text-text-primary hover:bg-background'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      activeView === tab.id ? 'bg-gold/20' : 'bg-background'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-border bg-background flex items-center gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search obligations, sections, documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="all">All Risk Levels</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <button
              onClick={fetchData}
              className="p-2 hover:bg-surface rounded-lg text-text-tertiary hover:text-text-primary transition-all"
            >
              <ArrowsClockwise size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <ArrowsClockwise size={32} className="animate-spin text-gold" />
              </div>
            ) : activeView === 'obligations' || activeView === 'review' ? (
              <ObligationsTable
                obligations={activeView === 'review' 
                  ? filteredObligations.filter(o => o.status === 'pending_review')
                  : filteredObligations
                }
                groupedByDocument={activeView === 'review' 
                  ? null
                  : groupedByDocument
                }
                expandedObligation={expandedObligation}
                setExpandedObligation={setExpandedObligation}
                onApprove={handleApprove}
                onReject={handleReject}
                approvingId={approvingId}
                rejectingId={rejectingId}
                showReviewActions={activeView === 'review' || filterStatus === 'pending_review'}
              />
            ) : activeView === 'controls' ? (
              <ControlsTable controls={controls} />
            ) : null}
            
            {/* Empty State */}
            {!loading && filteredObligations.length === 0 && (
              <div className="text-center py-20">
                <ClipboardText size={64} className="mx-auto text-text-tertiary mb-4 opacity-30" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">No Obligations Found</h3>
                <p className="text-text-secondary mb-4">
                  {selectedTheme !== 'all' 
                    ? `No obligations extracted for ${currentTheme?.label || 'this theme'} yet.`
                    : 'No obligations have been extracted yet.'}
                </p>
                <button
                  onClick={() => navigate('/aura-master-library')}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-lg hover:bg-gold-hover transition-all inline-flex items-center gap-2"
                >
                  <FolderOpen size={18} />
                  Go to Library to Extract
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Obligations Table Component
const ObligationsTable = ({ 
  obligations, 
  groupedByDocument, 
  expandedObligation, 
  setExpandedObligation,
  onApprove,
  onReject,
  approvingId,
  rejectingId,
  showReviewActions
}) => {
  if (groupedByDocument) {
    // Grouped view
    return (
      <div className="space-y-6">
        {Object.entries(groupedByDocument).map(([docName, docObligations]) => (
          <div key={docName} className="border border-border rounded-xl overflow-hidden">
            <div className="bg-surface px-4 py-3 border-b border-border flex items-center gap-3">
              <FileText size={20} className="text-gold" />
              <h3 className="font-semibold text-text-primary">{docName}</h3>
              <span className="text-xs text-text-tertiary bg-background px-2 py-0.5 rounded">
                {docObligations.length} obligations
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background text-xs text-text-tertiary uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left w-32">Section</th>
                    <th className="px-4 py-3 text-left">Obligation</th>
                    <th className="px-4 py-3 text-center w-24">Risk</th>
                    <th className="px-4 py-3 text-center w-24">Confidence</th>
                    <th className="px-4 py-3 text-center w-32">Status</th>
                    {showReviewActions && <th className="px-4 py-3 text-center w-32">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {docObligations.map((ob) => (
                    <ObligationRow
                      key={ob.id}
                      obligation={ob}
                      expanded={expandedObligation === ob.id}
                      onToggle={() => setExpandedObligation(expandedObligation === ob.id ? null : ob.id)}
                      onApprove={onApprove}
                      onReject={onReject}
                      approvingId={approvingId}
                      rejectingId={rejectingId}
                      showReviewActions={showReviewActions}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Flat view (for review queue)
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface text-xs text-text-tertiary uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left w-32">Section</th>
              <th className="px-4 py-3 text-left">Obligation</th>
              <th className="px-4 py-3 text-left w-48">Source Document</th>
              <th className="px-4 py-3 text-center w-24">Risk</th>
              <th className="px-4 py-3 text-center w-24">Confidence</th>
              {showReviewActions && <th className="px-4 py-3 text-center w-32">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {obligations.map((ob) => (
              <ObligationRow
                key={ob.id}
                obligation={ob}
                expanded={expandedObligation === ob.id}
                onToggle={() => setExpandedObligation(expandedObligation === ob.id ? null : ob.id)}
                onApprove={onApprove}
                onReject={onReject}
                approvingId={approvingId}
                rejectingId={rejectingId}
                showReviewActions={showReviewActions}
                showDocument
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Obligation Row Component
const ObligationRow = ({ 
  obligation, 
  expanded, 
  onToggle, 
  onApprove, 
  onReject, 
  approvingId, 
  rejectingId,
  showReviewActions,
  showDocument 
}) => {
  const riskRating = obligation.inherent_risk?.overall_rating || 'medium';
  const riskColors = RISK_COLORS[riskRating] || RISK_COLORS.medium;
  const statusColors = STATUS_COLORS[obligation.status] || STATUS_COLORS.pending_review;
  
  return (
    <>
      <tr 
        className="hover:bg-background/50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <span className="text-sm font-mono text-gold">{obligation.section_reference}</span>
        </td>
        <td className="px-4 py-3">
          <div className="max-w-lg">
            <p className="text-sm text-text-primary line-clamp-2">
              {obligation.plain_language_summary}
            </p>
            {expanded && (
              <div className="mt-2 p-3 bg-background rounded-lg">
                <p className="text-xs text-text-tertiary uppercase tracking-wide mb-1">Verbatim Text</p>
                <p className="text-sm text-text-secondary italic">"{obligation.verbatim_text}"</p>
                {obligation.trigger_event && (
                  <>
                    <p className="text-xs text-text-tertiary uppercase tracking-wide mb-1 mt-3">Trigger</p>
                    <p className="text-sm text-text-secondary">{obligation.trigger_event}</p>
                  </>
                )}
                {obligation.consequence_of_breach && (
                  <>
                    <p className="text-xs text-text-tertiary uppercase tracking-wide mb-1 mt-3">Consequence</p>
                    <p className="text-sm text-red-400">{obligation.consequence_of_breach}</p>
                  </>
                )}
              </div>
            )}
          </div>
        </td>
        {showDocument && (
          <td className="px-4 py-3">
            <span className="text-sm text-text-secondary line-clamp-1">{obligation.source_document_name}</span>
          </td>
        )}
        <td className="px-4 py-3 text-center">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${riskColors.bg} ${riskColors.text} ${riskColors.border} border`}>
            {riskRating.charAt(0).toUpperCase() + riskRating.slice(1)}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className={`text-sm font-mono ${
            obligation.extraction_confidence >= 85 ? 'text-emerald-400' :
            obligation.extraction_confidence >= 60 ? 'text-amber-400' : 'text-red-400'
          }`}>
            {Math.round(obligation.extraction_confidence)}%
          </span>
        </td>
        {!showReviewActions && (
          <td className="px-4 py-3 text-center">
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusColors.bg} ${statusColors.text} ${statusColors.border} border`}>
              {statusColors.label}
            </span>
          </td>
        )}
        {showReviewActions && (
          <td className="px-4 py-3">
            <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => onApprove(obligation.id)}
                disabled={approvingId === obligation.id}
                className="p-2 hover:bg-emerald-500/10 rounded-lg text-text-tertiary hover:text-emerald-400 transition-all disabled:opacity-50"
                title="Approve"
              >
                {approvingId === obligation.id ? (
                  <ArrowsClockwise size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
              </button>
              <button
                onClick={() => onReject(obligation.id)}
                disabled={rejectingId === obligation.id}
                className="p-2 hover:bg-red-500/10 rounded-lg text-text-tertiary hover:text-red-400 transition-all disabled:opacity-50"
                title="Reject"
              >
                {rejectingId === obligation.id ? (
                  <ArrowsClockwise size={16} className="animate-spin" />
                ) : (
                  <XCircle size={18} />
                )}
              </button>
            </div>
          </td>
        )}
      </tr>
    </>
  );
};

// Controls Table Component
const ControlsTable = ({ controls }) => {
  if (controls.length === 0) {
    return (
      <div className="text-center py-20">
        <Shield size={64} className="mx-auto text-text-tertiary mb-4 opacity-30" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">No Controls Yet</h3>
        <p className="text-text-secondary">
          Baseline controls are generated when obligations are extracted.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface text-xs text-text-tertiary uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Control Name</th>
              <th className="px-4 py-3 text-left">Description</th>
              <th className="px-4 py-3 text-center w-32">Type</th>
              <th className="px-4 py-3 text-center w-32">Owner</th>
              <th className="px-4 py-3 text-center w-32">Frequency</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {controls.map((control) => (
              <tr key={control.id} className="hover:bg-background/50">
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-text-primary">{control.control_name}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm text-text-secondary line-clamp-2">{control.control_description}</p>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    control.control_type === 'preventive' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                    control.control_type === 'detective' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                    'bg-blue-500/10 text-blue-400 border-blue-500/30'
                  } border`}>
                    {control.control_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-sm text-text-secondary">
                    {control.suggested_owner_role?.replace(/_/g, ' ') || '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-sm text-text-secondary">
                    {control.suggested_frequency || '-'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CRMPPage;
